import React from 'react';
import {
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import { Controller, Control, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

interface WebhookHeader {
  name: string;
  value: string;
}

interface WebhookQueryParameter {
  name: string;
  value: string;
}

export interface ConsumerFormData {
  name: string;
  webhook: {
    url: string;
    method: string;
    headers: WebhookHeader[];
    queryParameters: WebhookQueryParameter[];
  };
  batchCapacity: number;
  batchMinInterval: number;
}

interface ConsumerFormFieldsProps {
  control: Control<ConsumerFormData>;
  errors: FieldErrors<ConsumerFormData>;
  headerFields: UseFieldArrayReturn<ConsumerFormData, 'webhook.headers', 'id'>['fields'];
  appendHeader: (value: WebhookHeader) => void;
  removeHeader: (index: number) => void;
  queryParamFields: UseFieldArrayReturn<ConsumerFormData, 'webhook.queryParameters', 'id'>['fields'];
  appendQueryParam: (value: WebhookQueryParameter) => void;
  removeQueryParam: (index: number) => void;
  autoFocus?: boolean;
}

export const ConsumerFormFields: React.FC<ConsumerFormFieldsProps> = ({
  control,
  errors,
  headerFields,
  appendHeader,
  removeHeader,
  queryParamFields,
  appendQueryParam,
  removeQueryParam,
  autoFocus = false,
}) => {
  return (
    <>
      <Controller
        name="name"
        control={control}
        rules={{ 
          required: 'Consumer name is required',
          validate: (value) => value.trim() !== '' || 'Consumer name cannot be empty'
        }}
        render={({ field }) => (
          <TextField
            {...field}
            autoFocus={autoFocus}
            margin="dense"
            label="Consumer Name"
            fullWidth
            variant="outlined"
            required
            error={!!errors.name}
            helperText={errors.name?.message || "Enter a descriptive name for the consumer"}
            sx={{ mb: 2 }}
          />
        )}
      />

      <Controller
        name="batchCapacity"
        control={control}
        rules={{ 
          required: 'Batch capacity is required',
          min: { value: 1, message: 'Batch capacity must be at least 1' },
          max: { value: 1000, message: 'Batch capacity cannot exceed 1000' }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            margin="dense"
            label="Batch Capacity"
            type="number"
            fullWidth
            variant="outlined"
            error={!!errors.batchCapacity}
            helperText={errors.batchCapacity?.message || "Maximum number of messages per batch"}
            slotProps={{ htmlInput: { min: 1, max: 1000 } }}
            sx={{ mb: 2 }}
            onChange={(e) => field.onChange(Number.parseInt(e.target.value) || 10)}
          />
        )}
      />

      <Controller
        name="batchMinInterval"
        control={control}
        rules={{ 
          required: 'Batch min interval is required',
          min: { value: 100, message: 'Batch min interval must be at least 100ms' },
          max: { value: 60000, message: 'Batch min interval cannot exceed 60000ms' }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            margin="dense"
            label="Batch Min Interval (ms)"
            type="number"
            fullWidth
            variant="outlined"
            error={!!errors.batchMinInterval}
            helperText={errors.batchMinInterval?.message || "Minimum time between batches in milliseconds"}
            slotProps={{ htmlInput: { min: 100, max: 60000 } }}
            sx={{ mb: 2 }}
            onChange={(e) => field.onChange(Number.parseInt(e.target.value) || 1000)}
          />
        )}
      />
      
      {/* Webhook Configuration Section */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>WEBHOOK</Typography>
      
      <Controller
        name="webhook.url"
        control={control}
        rules={{ 
          required: 'Webhook URL is required',
          validate: (value) => {
            if (!value.trim()) {
              return 'Webhook URL cannot be empty';
            }
            try {
              new URL(value);
              return true;
            } catch {
              return 'Please enter a valid URL';
            }
          }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            required
            error={!!errors.webhook?.url}
            helperText={errors.webhook?.url?.message || "Enter the webhook URL that will receive notifications"}
            placeholder="https://your-api.com/webhooks/notifications"
            sx={{ mb: 2 }}
          />
        )}
      />

      <Controller
        name="webhook.method"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            margin="dense"
            label="Method"
            fullWidth
            variant="outlined"
            disabled
            helperText="HTTP method is fixed to POST"
            sx={{ mb: 2 }}
          />
        )}
      />

      {/* Headers Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Headers
          <IconButton
            size="small"
            color="primary"
            onClick={() => appendHeader({ name: '', value: '' })}
            aria-label="Add header"
          >
            <AddIcon />
          </IconButton>
        </Typography>
        
        {headerFields.map((field, index) => (
          <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Controller
              name={`webhook.headers.${index}.name`}
              control={control}
              rules={{ required: 'Header name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  error={!!errors.webhook?.headers?.[index]?.name}
                  helperText={errors.webhook?.headers?.[index]?.name?.message}
                />
              )}
            />
            <Controller
              name={`webhook.headers.${index}.value`}
              control={control}
              rules={{ required: 'Header value is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Value"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  error={!!errors.webhook?.headers?.[index]?.value}
                  helperText={errors.webhook?.headers?.[index]?.value?.message}
                />
              )}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => removeHeader(index)}
              aria-label="Remove header"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        
        {headerFields.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No headers configured. Click + to add a header.
          </Typography>
        )}
      </Box>

      {/* Query Parameters Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Query Parameters
          <IconButton
            size="small"
            color="primary"
            onClick={() => appendQueryParam({ name: '', value: '' })}
            aria-label="Add query parameter"
          >
            <AddIcon />
          </IconButton>
        </Typography>
        
        {queryParamFields.map((field, index) => (
          <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Controller
              name={`webhook.queryParameters.${index}.name`}
              control={control}
              rules={{ required: 'Query parameter name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  error={!!errors.webhook?.queryParameters?.[index]?.name}
                  helperText={errors.webhook?.queryParameters?.[index]?.name?.message}
                />
              )}
            />
            <Controller
              name={`webhook.queryParameters.${index}.value`}
              control={control}
              rules={{ required: 'Query parameter value is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Value"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  error={!!errors.webhook?.queryParameters?.[index]?.value}
                  helperText={errors.webhook?.queryParameters?.[index]?.value?.message}
                />
              )}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => removeQueryParam(index)}
              aria-label="Remove query parameter"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        
        {queryParamFields.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No query parameters configured. Click + to add a query parameter.
          </Typography>
        )}
      </Box>
    </>
  );
};
